/** Firebase Functions client errors worth retrying (transient network / overloaded backends). */
const RETRYABLE_CODES = new Set(['resource-exhausted']);

export function getFirebaseCallableErrorCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const c = (error as { code?: string }).code;
    return typeof c === 'string' ? c : '';
  }
  return '';
}

function normalizeFirebaseCallableCode(error: unknown): string {
  return getFirebaseCallableErrorCode(error).replace(/^functions\//, '');
}

/** User-facing message from a Firebase callable rejection (not generic "internal" when avoidable). */
export function getCallableErrorMessage(error: unknown, fallback: string): string {
  if (typeof error !== 'object' || error === null) {
    return fallback;
  }
  const e = error as { message?: string; details?: unknown };
  const code = normalizeFirebaseCallableCode(error);
  const message = typeof e.message === 'string' ? e.message.trim() : '';
  const detailsMessage = typeof e.details === 'string' ? e.details.trim() : '';
  if (code && code !== 'internal' && message) {
    return message;
  }
  if (code === 'failed-precondition' && detailsMessage) {
    return detailsMessage;
  }
  if (code === 'internal' && detailsMessage) {
    return detailsMessage;
  }
  if (code === 'internal' && message && !/^internal(\s*\[\d+\])?$/i.test(message)) {
    return message;
  }
  if (message && !/^internal(\s*\[\d+\])?$/i.test(message)) {
    return message;
  }
  return fallback;
}

/** Client timeout or connection drop before a callable response (HTTP status 0 in message). */
export function isCallableTransportError(error: unknown): boolean {
  const code = normalizeFirebaseCallableCode(error);
  if (code === 'deadline-exceeded' || code === 'unavailable') {
    return true;
  }
  if (code === 'internal') {
    const msg = error instanceof Error ? error.message : String(error);
    return msg.includes('[0]');
  }
  return false;
}

function isRetryableCallableError(error: unknown): boolean {
  return isCallableTransportError(error) || RETRYABLE_CODES.has(normalizeFirebaseCallableCode(error));
}

/** Runs `run` up to 3 times on retryable Firebase callable errors; rethrows last error. */
export async function invokeCallableWithRetry<T>(run: () => Promise<T>): Promise<T> {
  const backoffMs = [600, 2000];
  let lastError: unknown;
  for (let attempt = 0; attempt < backoffMs.length + 1; attempt++) {
    try {
      return await run();
    } catch (e) {
      lastError = e;
      if (!isRetryableCallableError(e) || attempt === backoffMs.length) throw e;
      await new Promise<void>((resolve) => setTimeout(resolve, backoffMs[attempt] ?? 1000));
    }
  }
  throw lastError;
}
